import React, {useEffect, useState, useRef, useMemo} from 'react' // Added useMemo
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {Tooltip} from 'bootstrap' // Added Tooltip import
import {UserModel} from '../../auth'
import {ITotalInTransitBody} from '../core/_models' // Import the new interface
import {getInTransitReports} from '../core/_requests' // Assuming this path is correct
import {storeCurrencyLabel} from '../../common/currencyLabel'

type Props = {
  className?: string
  language: string
  currentUser: UserModel | undefined
}

const InTransitCard: React.FC<Props> = ({language, currentUser, className}) => {
  const {formatMessage} = useIntl()
  // Use the imported interface for state typing and provide default values
  const [inTransitTotal, setInTransitTotal] = useState<ITotalInTransitBody>({
    total_quantity: '0',
    total_sale_price: '0',
    info: '',
  })
  const [loading, setLoading] = useState<boolean>(true) // Added loading state
  const tooltipTriggerRef = useRef<HTMLElement | null>(null) // Added ref for tooltip trigger

  useEffect(() => {
    const getData = async () => {
      setLoading(true); // Set loading true at the start
      try {
        const {data} = await getInTransitReports(currentUser?.token, language)
        // Check for data, data.total_in_transit, and data.info
        if (data && data.total_in_transit) {
          // Access nested total_in_transit and top-level info
          setInTransitTotal({
            total_quantity: data.total_in_transit.total_quantity || '0',
            total_sale_price: data.total_in_transit.total_sale_price || '0',
            info: data.info || '', // Access top-level info field
          })
        } else {
           // Handle case where data structure is not as expected
           console.log("API Response - data, total_in_transit, or info missing");
           setInTransitTotal({ total_quantity: '0', total_sale_price: '0', info: '' });
        }
      } catch (error) {
        console.error('Error fetching in-transit data:', error)
        // Handle error state if needed, ensuring 'info' is included
        setInTransitTotal({ total_quantity: '0', total_sale_price: '0', info: '' });
      } finally {
         setLoading(false); // Set loading false when done
      }
    }
    getData()
  }, [currentUser, language])

  // Effect for initializing tooltip after loading is complete
  useEffect(() => {
    if (!loading && tooltipTriggerRef.current) {
      const el = tooltipTriggerRef.current
      const existingTooltip = Tooltip.getInstance(el)
      if (existingTooltip) {
        existingTooltip.dispose() // Remove old instance
      }
      // Initialize new tooltip
      new Tooltip(el, {
        html: true,
        customClass: 'tooltip-light', // Use light theme like TransactionSummaryCard
      })
    }
    // Cleanup function
    return () => {
      if (tooltipTriggerRef.current) {
        const existingTooltip = Tooltip.getInstance(tooltipTriggerRef.current)
        if (existingTooltip) {
          existingTooltip.dispose()
        }
      }
    }
  }, [loading, inTransitTotal.info]) // Re-run if loading changes or info text changes

  const { dateStart, dateEnd } = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // 0-based
  
    // Function to format date as YYYY-MM-DD
    const formatLocalDate = (date: Date): string => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
  
    // First day of the month two months ago
    const firstDay = new Date(year, month - 2, 1); // Changed month - 1 to month - 2
  
    // Last day of the current month
    const lastDay = new Date(year, month + 1, 0);
  
    return {
      dateStart: formatLocalDate(firstDay),
      dateEnd: formatLocalDate(lastDay),
    };
  }, []);
  

  return (
    <div className={`hsb-card ${className}`}>
      <div>
        <div className='hsb-card-head'>
          <span className='hsb-chip hsb-chip--secondary'>
            <span className='material-symbols-outlined'>local_shipping</span>
          </span>
        </div>
        <span className='hsb-kpi-label'>
          {formatMessage({id: 'total_sale_price_form'})}
          <span className='fw-semibold' style={{padding: '0 4px'}}>
            {inTransitTotal.total_quantity}
          </span>
          {formatMessage({id: 'total_transit_product_quantities'})}
          <i
            ref={tooltipTriggerRef} // Add ref
            className='fas fa-info-circle hsb-info-icon'
            data-bs-toggle='tooltip'
            data-bs-placement='top'
            data-bs-custom-class='tooltip-light' // Use light theme
            data-bs-html='true'
            title={inTransitTotal.info || ''} // Set title from state
          ></i>
        </span>
        <h2 className='hsb-kpi-value'>
          {inTransitTotal.total_sale_price} <span className='hsb-kpi-unit'>{storeCurrencyLabel(formatMessage)}</span>
        </h2>
      </div>
      <Link
        to={`/sales/in-transit?filter_date_start=${dateStart}&filter_date_end=${dateEnd}`}
        className='hsb-link hsb-link--secondary'
      >
        {formatMessage({id: 'view_in_transit_products_list'})}
        <span className='material-symbols-outlined'>arrow_forward</span>
      </Link>
    </div>
  )
}

export default InTransitCard
